import axios from 'axios'
import {
  MOCK_PROJECTS,
  getMockProject,
  createMockProject,
  getMockMessages,
  createMockMessage,
  getMockProjectMeta,
  saveMockProjectMeta,
  getMockWorkflowEvents,
  getMockWorkflowStatus,
  getMockWorkflowSnapshot,
} from './mockData'

/**
 * For developer: if TRUE, mock data will be used instead of API requests
 */
// Local dev toggle: run `VITE_USE_MOCK=1 npm run client` to use mocked auth/data
// (lets you browse the app — projects, ProjectView, Monitoring — without a backend).
// Defaults OFF so normal builds hit the real API.
const USE_MOCK_DATA = import.meta.env?.VITE_USE_MOCK === '1'

const client = axios.create({
  // baseURL left relative; nginx will proxy to orchestrator
  headers: { 'Content-Type': 'application/json' },
  timeout: 20000,
  withCredentials: true,
})

// Lightweight debug toggle: open DevTools and run localStorage.setItem('debug_api','1')
try {
  if (typeof window !== 'undefined') {
    window.__DEBUG_API__ = window.localStorage?.getItem('debug_api') === '1'
  }
} catch {}

client.interceptors.request.use(cfg => {
  try {
    if (typeof window !== 'undefined' && window.__DEBUG_API__) {
      // Avoid logging binary FormData
      const isForm = cfg.headers && String(cfg.headers['Content-Type'] || '').includes('multipart/form-data')

      console.log('[api:req]', cfg.method?.toUpperCase(), cfg.url, isForm ? '[FormData]' : cfg.data)
    }
  } catch {}
  return cfg
})

client.interceptors.response.use(
  res => {
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_API__) {
        console.log('[api:res]', res.status, res.config?.url, res.data)
      }
    } catch {}
    return res
  },
  err => {
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_API__) {
        console.log('[api:err]', err?.response?.status, err?.config?.url, err?.response?.data || err?.message)
      }
    } catch {}
    return Promise.reject(err)
  }
)

function setMockUserCookie(email) {
  if (typeof document === 'undefined') return
  document.cookie = `mod_user=${encodeURIComponent(email || '')}; Path=/; SameSite=Lax; Max-Age=2592000`
  try {
    window.localStorage?.setItem('mod_mock_user', email || '')
  } catch {}
}

function getMockUserCookie() {
  if (typeof document === 'undefined') return ''
  const m = document.cookie.match(/(?:^|; )mod_user=([^;]*)/)
  if (m && m[1]) return decodeURIComponent(m[1])
  try {
    return window.localStorage?.getItem('mod_mock_user') || ''
  } catch {
    return ''
  }
}

function clearMockUserCookie() {
  if (typeof document !== 'undefined') {
    document.cookie = 'mod_user=; Path=/; Max-Age=0'
  }
  try {
    window.localStorage?.removeItem('mod_mock_user')
  } catch {}
}

export async function requestCode(email) {
  if (USE_MOCK_DATA) {
    return Promise.resolve({ ok: true, code: '123456' })
  }
  const { data } = await client.post('/auth/request-code', { email })
  return data
}

export async function verifyCode(email, code) {
  if (USE_MOCK_DATA) {
    if (!/^\d{6}$/.test(code || '')) {
      return Promise.reject({ response: { data: { detail: 'Invalid code' } } })
    }
    return Promise.resolve({ ok: true })
  }
  const { data } = await client.post('/auth/verify-code', { email, code })
  return data
}

export async function setPassword(email, code, password, marketingConsent = false) {
  if (USE_MOCK_DATA) {
    setMockUserCookie(email)
    return Promise.resolve({ ok: true, email })
  }
  const { data } = await client.post('/auth/set-password', {
    email,
    code,
    password,
    marketing_consent: !!marketingConsent,
  })
  return data
}

export async function login(email, password) {
  if (USE_MOCK_DATA) {
    if (!email || !password) {
      return Promise.reject({ response: { data: { detail: 'Email and password required' } } })
    }
    setMockUserCookie(email)
    return Promise.resolve({ ok: true, email })
  }
  const { data } = await client.post('/auth/login', { email, password })
  return data
}

export async function me() {
  if (USE_MOCK_DATA) {
    const email = getMockUserCookie()
    if (!email) {
      return Promise.reject({ response: { status: 401, data: { detail: 'unauthorized' } } })
    }
    return Promise.resolve({ email })
  }
  const { data } = await client.get('/auth/me')
  return data
}

export async function logout() {
  if (USE_MOCK_DATA) {
    clearMockUserCookie()
    return Promise.resolve({ ok: true })
  }
  const { data } = await client.post('/auth/logout')
  return data
}

// Выход на всех устройствах: удаляет все сессии пользователя (включая текущую)
export async function logoutEverywhere() {
  if (USE_MOCK_DATA) {
    clearMockUserCookie()
    return Promise.resolve({ ok: true })
  }
  const { data } = await client.post('/auth/logout-all')
  return data
}

export async function listProjects() {
  if (USE_MOCK_DATA) return Promise.resolve(MOCK_PROJECTS)

  const { data } = await client.get('/api/projects')
  return data.projects || []
}

export async function createProject(name) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(createMockProject(name))
  }
  const { data } = await client.post('/api/projects', { name })
  return data
}

export async function getProject(id) {
  if (USE_MOCK_DATA) {
    const project = getMockProject(id)
    if (!project) {
      return Promise.reject({ response: { status: 404, data: { detail: 'not found' } } })
    }
    return Promise.resolve(project)
  }
  const { data } = await client.get(`/api/projects/${id}`)
  return data
}

export async function listMessages(id) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(getMockMessages(id))
  }
  const { data } = await client.get(`/api/projects/${id}/messages`)
  return data.messages || []
}

export async function sendMessage(id, role, content) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(createMockMessage(id, role, content))
  }
  const { data } = await client.post(`/api/projects/${id}/messages`, { role, content })
  return data
}

export async function sendChatMessage(id, message, context) {
  const { data } = await client.post(`/api/projects/${id}/chat`, { message, context }, { timeout: 30000 })
  return data
}

// Chunked upload configuration
const CHUNK_SIZE = 50 * 1024 * 1024 // 50MB per chunk (larger = fewer HTTP requests, less overhead)
const PARALLEL_CHUNKS = 2 // Upload 2 chunks in parallel (stable for large chunks)
const CHUNKED_THRESHOLD = 100 * 1024 * 1024 // Use chunked upload for files > 100MB

/**
 * Upload a dataset file with progress tracking
 * Uses chunked upload for large files (>50MB) for better performance
 * @param {number} id - Project ID
 * @param {File} file - File to upload
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<{ok: boolean, dataset_path: string, filename: string}>}
 */
export async function uploadDataset(id, file, onProgress = null) {
  if (USE_MOCK_DATA) {
    // Simulate file upload
    const filename = file?.name || 'dataset.csv'
    const datasetPath = `/mock/projects/${id}/${filename}`
    if (onProgress) onProgress(100)
    return Promise.resolve({
      ok: true,
      dataset_path: datasetPath,
      filename: filename,
    })
  }

  // Use chunked upload for large files
  if (file.size > CHUNKED_THRESHOLD) {
    return uploadDatasetChunked(id, file, onProgress)
  }

  // Regular upload for small files (with progress if XMLHttpRequest available)
  if (onProgress && typeof XMLHttpRequest !== 'undefined') {
    return uploadDatasetWithProgress(id, file, onProgress)
  }

  // Fallback to simple upload
  const form = new FormData()
  form.append('file', file)
  const { data } = await client.post(`/api/projects/${id}/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0,
  })
  if (onProgress) onProgress(100)
  return data
}

/**
 * Upload with XMLHttpRequest for progress tracking (small files)
 */
async function uploadDatasetWithProgress(id, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    form.append('file', file)

    xhr.upload.addEventListener('progress', e => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          resolve({ ok: true })
        }
      } else {
        const err = new Error('upload_failed')
        err.status = xhr.status
        reject(err)
      }
    })

    xhr.addEventListener('error', () => reject(new Error('upload_failed')))
    xhr.open('POST', `/api/projects/${id}/upload`)
    xhr.withCredentials = true
    xhr.send(form)
  })
}

/**
 * Chunked upload for large files with real-time progress tracking
 */
async function uploadDatasetChunked(id, file, onProgress) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
  const filename = file.name
  const totalSize = file.size

  // Track progress per chunk (bytes uploaded)
  const chunkProgress = new Array(totalChunks).fill(0)
  const chunkSizes = []
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    chunkSizes.push(end - start)
  }

  // Calculate and report total progress
  const reportProgress = () => {
    if (!onProgress) return
    const uploadedBytes = chunkProgress.reduce((a, b) => a + b, 0)
    // 95% for upload, 5% for merge
    const percent = Math.round((uploadedBytes / totalSize) * 95)
    onProgress(Math.min(percent, 95))
  }

  // 1. Initialize upload
  const initRes = await client.post(`/api/projects/${id}/upload/init`, {
    filename,
    totalSize: file.size,
    totalChunks,
  })
  const { uploadId } = initRes.data

  // 2. Upload chunks in parallel batches with real-time progress
  const chunkIndices = Array.from({ length: totalChunks }, (_, i) => i)

  // Process chunks in batches of PARALLEL_CHUNKS
  for (let i = 0; i < chunkIndices.length; i += PARALLEL_CHUNKS) {
    const batch = chunkIndices.slice(i, i + PARALLEL_CHUNKS)
    await Promise.all(
      batch.map(async chunkIndex => {
        const start = chunkIndex * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, file.size)
        const chunk = file.slice(start, end)
        const chunkSize = chunkSizes[chunkIndex]

        const form = new FormData()
        form.append('uploadId', uploadId)
        form.append('chunkIndex', chunkIndex.toString())
        form.append('chunk', chunk)

        await client.post(`/api/projects/${id}/upload/chunk`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0,
          onUploadProgress: progressEvent => {
            // Update progress for this specific chunk
            chunkProgress[chunkIndex] = progressEvent.loaded
            reportProgress()
          },
        })

        // Mark chunk as fully uploaded
        chunkProgress[chunkIndex] = chunkSize
        reportProgress()
      })
    )
  }

  // 3. Complete upload (merge chunks)
  const completeRes = await client.post(`/api/projects/${id}/upload/complete`, { uploadId })
  if (onProgress) onProgress(100)

  return completeRes.data
}

export async function startWorkflow(id, payload) {
  if (USE_MOCK_DATA) {
    // Generate mock workflow ID
    const workflowId = `wf-mock-${Date.now()}`
    const jobId = `job-mock-${Date.now()}`

    // Update project meta with workflow ID
    const meta = getMockProjectMeta(id)
    saveMockProjectMeta(id, {
      ...meta,
      active_workflow_id: workflowId,
      last_workflow_id: workflowId,
      last_input: payload,
    })

    // Add system message
    createMockMessage(id, 'system', `Workflow started: ${workflowId}`)

    return Promise.resolve({
      ok: true,
      workflow_id: workflowId,
      job_id: jobId,
    })
  }
  // Payload format: { tables: [{ path, columns, role }], user_prompt }
  // Legacy format (backward compatibility): { dataset_path, user_prompt, columns_schema }
  const { data } = await client.post(`/api/projects/${id}/workflow/start`, payload)
  return data
}

// Orchestrator workflow (proxied via nginx to :8103)

// Atomic endpoint: status + events + snapshot in one request (no race conditions)
export async function getWorkflowLive(workflowId, eventsLimit = 200) {
  if (USE_MOCK_DATA) {
    return Promise.resolve({
      status: getMockWorkflowStatus(workflowId),
      events: getMockWorkflowEvents(workflowId)?.events || [],
      snapshot: getMockWorkflowSnapshot(workflowId),
    })
  }
  const { data } = await client.get(`/api/workflow/${workflowId}/live`, { params: { events_limit: eventsLimit } })
  return data
}

// DEPRECATED: Use getWorkflowLive instead
export async function getWorkflowStatus(workflowId) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(getMockWorkflowStatus(workflowId))
  }
  const { data } = await client.get(`/api/workflow/${workflowId}/status`)
  return data
}

// DEPRECATED: Use getWorkflowLive instead
export async function getWorkflowEvents(workflowId, limit = 200) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(getMockWorkflowEvents(workflowId))
  }
  const { data } = await client.get(`/api/workflow/${workflowId}/events`, { params: { limit } })
  return data
}

// DEPRECATED: Use getWorkflowLive instead
export async function getWorkflowSnapshot(workflowId) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(getMockWorkflowSnapshot(workflowId))
  }
  const { data } = await client.get(`/api/workflow/${workflowId}`)
  return data
}

// Human-in-the-loop and control
export async function humanResolve(workflowId, payload) {
  const { data } = await client.post(`/api/workflow/${workflowId}/human/resolve`, payload)
  return data
}

export async function workflowOverride(workflowId, payload) {
  const { data } = await client.post(`/api/workflow/${workflowId}/override`, payload)
  return data
}

export async function workflowPause(workflowId) {
  const { data } = await client.post(`/api/workflow/${workflowId}/pause`)
  return data
}

export async function workflowResume(workflowId) {
  const { data } = await client.post(`/api/workflow/${workflowId}/resume`)
  return data
}

// Project meta (schema/settings)
export async function getProjectMeta(id) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(getMockProjectMeta(id))
  }
  const { data } = await client.get(`/api/projects/${id}/meta`)
  return data
}

export async function saveProjectMeta(id, payload) {
  if (USE_MOCK_DATA) {
    return Promise.resolve(saveMockProjectMeta(id, payload))
  }
  const { data } = await client.post(`/api/projects/${id}/meta`, payload)
  return data
}

// Dataset inspection (proxy to sandbox)
export async function inspectDataset(path) {
  const { data } = await client.get('/api/datasets/inspect', { params: { path } })
  return data
}

// CRM Connections
export async function listConnections(userId) {
  const { data } = await client.get('/api/crm/connections', { params: { user_id: userId } })
  return data
}

export async function createConnection(userId, provider, callbackUrl) {
  const { data } = await client.post('/api/crm/connections', {
    user_id: userId, provider, callback_url: callbackUrl,
  })
  return data
}

export async function deleteConnection(connectionId, userId) {
  const { data } = await client.delete(`/api/crm/connections/${connectionId}`, {
    params: { user_id: userId },
  })
  return data
}

export async function listCrmObjects(connectionId) {
  const { data } = await client.get(`/api/crm/connections/${connectionId}/objects`)
  return data
}

export async function triggerSync(connectionId, userId, objects) {
  const { data } = await client.post(`/api/crm/connections/${connectionId}/sync`, { objects }, {
    params: { user_id: userId },
  })
  return data
}

export async function listCrmDatasets(connectionId, userId) {
  const { data } = await client.get(`/api/crm/connections/${connectionId}/datasets`, {
    params: { user_id: userId },
  })
  return data
}
